<template>
  <div class="page">
    <div class="page-inner">
      <header class="page-header">
        <div>
          <h1 class="page-title">{{ pageTitle }}</h1>
          <p class="sub">
            학교 또는 기관명과 접수일 기준 월 범위를 설정하여 블랜디드 /
            테크센터 A/S 이력을 조회할 수 있습니다.
          </p>
        </div>
      </header>

      <!-- 검색 영역 -->
      <section class="card search-card">
        <div class="search-grid">
          <div class="search-field school-field">
            <label for="schoolName">학교 or 기관명</label>
            <input
              id="schoolName"
              v-model.trim="schoolName"
              type="text"
              class="input"
              placeholder="학교 또는 기관명을 입력하세요"
              @keyup.enter="handleSearch"
            />
          </div>

          <div class="search-field month-field">
            <label for="startMonth">접수일자 기준</label>
            <input
              id="startMonth"
              v-model="startMonth"
              type="month"
              class="input month-input"
            />
          </div>

          <div class="search-field month-field month-field-end">
            <label for="endMonth" class="sr-only">종료월</label>
            <div class="month-end-wrap">
              <span class="range-tilde">~</span>
              <input
                id="endMonth"
                v-model="endMonth"
                type="month"
                class="input month-input"
              />
            </div>
          </div>

          <div class="search-actions">
            <button
              type="button"
              class="btn btn-primary"
              :disabled="isLoading"
              @click="handleSearch"
            >
              조회
            </button>

            <button
              type="button"
              class="btn btn-outline"
              :disabled="isLoading"
              @click="resetSearch"
            >
              초기화
            </button>

            <button
              type="button"
              class="btn btn-dark"
              :disabled="isLoading || !hasAnyResult || !searchedSchoolName"
              @click="openPdfPreview"
            >
              PDF 미리보기
            </button>
          </div>
        </div>
      </section>

      <!-- 로딩 -->
      <section v-if="isLoading" class="card state-card">
        <div class="loading-box">
          <span class="spinner"></span>
          <span>데이터를 불러오는 중입니다.</span>
        </div>
      </section>

      <!-- 결과 없음 -->
      <section v-else-if="searched && !hasAnyResult" class="card state-card">
        검색 결과가 없습니다.
      </section>

      <template v-else>
        <!-- 블랜디드 -->
        <section v-if="blendRows.length" class="card table-card result-section">
          <div class="section-head">
            <h2 class="section-title">블랜디드 조회결과</h2>
            <div class="section-count">총 {{ blendRows.length }}건</div>
          </div>

          <div class="table-wrap desktop-only">
            <table class="intake-table">
              <thead>
                <tr>
                  <th
                    v-for="col in visibleListColumns"
                    :key="`blend-head-${col.key}`"
                    :class="col.className"
                  >
                    {{ col.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in blendRows" :key="row.id" class="data-row">
                  <td
                    v-for="col in visibleListColumns"
                    :key="`blend-row-${row.id}-${col.key}`"
                  >
                    <div
                      class="cell-text"
                      :class="{ 'multiline-text': col.multiline }"
                      :title="col.value(row)"
                    >
                      {{ col.value(row) }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="mobile-only">
            <div class="mobile-list">
              <article
                v-for="row in blendRows"
                :key="row.id"
                class="mobile-item card"
              >
                <div class="mobile-top">
                  <div class="mobile-title">
                    {{ row.productName || "-" }}
                  </div>
                </div>

                <div class="mobile-meta">
                  <div
                    v-for="col in visibleMobileMetaColumns"
                    :key="`blend-mobile-meta-${row.id}-${col.key}`"
                  >
                    <b>{{ col.label }}</b> {{ col.value(row) }}
                  </div>
                </div>

                <div
                  v-for="col in visibleMobileBlockColumns"
                  :key="`blend-mobile-block-${row.id}-${col.key}`"
                  class="mobile-block"
                >
                  <div class="mobile-label">{{ col.label }}</div>
                  <div class="mobile-value">
                    {{ col.value(row) }}
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>

        <!-- 테크센터 -->
        <section v-if="techRows.length" class="card table-card result-section">
          <div class="section-head">
            <h2 class="section-title">테크센터 조회결과</h2>
            <div class="section-count">총 {{ techRows.length }}건</div>
          </div>

          <div class="table-wrap desktop-only">
            <table class="intake-table">
              <thead>
                <tr>
                  <th
                    v-for="col in visibleListColumns"
                    :key="`tech-head-${col.key}`"
                    :class="col.className"
                  >
                    {{ col.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in techRows" :key="row.id" class="data-row">
                  <td
                    v-for="col in visibleListColumns"
                    :key="`tech-row-${row.id}-${col.key}`"
                  >
                    <div
                      class="cell-text"
                      :class="{ 'multiline-text': col.multiline }"
                      :title="col.value(row)"
                    >
                      {{ col.value(row) }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="mobile-only">
            <div class="mobile-list">
              <article
                v-for="row in techRows"
                :key="row.id"
                class="mobile-item card"
              >
                <div class="mobile-top">
                  <div class="mobile-title">
                    {{ row.productName || "-" }}
                  </div>
                </div>

                <div class="mobile-meta">
                  <div
                    v-for="col in visibleMobileMetaColumns"
                    :key="`tech-mobile-meta-${row.id}-${col.key}`"
                  >
                    <b>{{ col.label }}</b> {{ col.value(row) }}
                  </div>
                </div>

                <div
                  v-for="col in visibleMobileBlockColumns"
                  :key="`tech-mobile-block-${row.id}-${col.key}`"
                  class="mobile-block"
                >
                  <div class="mobile-label">{{ col.label }}</div>
                  <div class="mobile-value">
                    {{ col.value(row) }}
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>
      </template>
    </div>

    <!-- PDF 미리보기 -->
    <div
      v-if="pdfModalVisible"
      class="modal-overlay"
      @click.self="closePdfPreview"
    >
      <div class="modal-card">
        <div class="modal-header">
          <div>
            <h2 class="modal-title">PDF 미리보기</h2>
            <p class="modal-sub">
              {{ pdfTitle }} · 총 {{ pdfPageChunks.length || 0 }}페이지
            </p>
          </div>

          <button
            type="button"
            class="modal-close"
            @click="closePdfPreview"
            aria-label="닫기"
          >
            ×
          </button>
        </div>

        <div class="modal-body">
          <iframe
            v-if="pdfPreviewUrl"
            :src="pdfPreviewUrl"
            class="pdf-frame"
            title="PDF Preview"
          ></iframe>

          <div v-else class="pdf-loading">
            <div class="loading-box">
              <span class="spinner"></span>
              <span>PDF를 생성하는 중입니다.</span>
            </div>
          </div>

          <!-- 캡처용 숨김 영역 -->
          <div class="capture-area">
            <div class="pdf-capture-stack" ref="pdfCapturePagesRef">
              <div
                v-for="(page, pageIndex) in pdfPageChunks"
                :key="`pdf-page-${pageIndex}`"
                class="pdf-page"
              >
                <div class="pdf-page-head">
                  <div class="pdf-title">{{ pdfTitle }}</div>
                  <div class="pdf-page-meta">
                    <span
                      >{{ pageIndex + 1 }} / {{ pdfPageChunks.length }}</span
                    >
                  </div>
                </div>

                <div class="pdf-section-label">{{ page.sectionLabel }}</div>

                <table class="pdf-table">
                  <thead>
                    <tr>
                      <th
                        v-for="col in visiblePdfColumns"
                        :key="`pdf-head-${pageIndex}-${col.key}`"
                        :class="col.pdfClassName"
                      >
                        {{ col.label }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in page.rows" :key="item.pdfRowId">
                      <td
                        v-for="col in visiblePdfColumns"
                        :key="`pdf-row-${item.pdfRowId}-${col.key}`"
                        :class="{
                          left: col.left,
                          pre: col.multiline
                        }"
                      >
                        {{ col.value(item) }}
                      </td>
                    </tr>

                    <tr v-if="!page.rows.length">
                      <td :colspan="visiblePdfColumns.length">
                        검색 결과가 없습니다.
                      </td>
                    </tr>
                  </tbody>
                </table>

                <div class="pdf-footer">출력일시 {{ printedAt }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-outline"
            @click="closePdfPreview"
          >
            닫기
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="isDownloadingPdf"
            @click="downloadPdf"
          >
            {{ isDownloadingPdf ? "다운로드 중..." : "다운로드" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { computed, inject, nextTick, onBeforeUnmount, ref } from "vue";
import { requestApi } from "@/api";

const showToast = inject("showToast");

const schoolName = ref("");
const searchedSchoolName = ref("");
const isLoading = ref(false);
const searched = ref(false);

const techRows = ref([]);
const blendRows = ref([]);

const pdfModalVisible = ref(false);
const pdfPreviewUrl = ref("");
const pdfCapturePagesRef = ref(null);
const pdfPageChunks = ref([]);
const isDownloadingPdf = ref(false);

/**
 * =========================
 * 컬럼 표시 여부 설정
 * =========================
 * 목록용 / PDF용 따로 관리
 */
const listColumnVisible = ref({
  seq: false,
  receivedDate: true,
  schoolName: false,
  customerName: false,
  productName: true,
  manufacturerModel: true,
  classroom: false,
  receiptContent: true,
  visitDate: true,
  progressStatus: true,
  processResult: true,
  completedDate: true
});

const pdfColumnVisible = ref({
  seq: false,
  receivedDate: true,
  schoolName: false,
  customerName: false,
  productName: true,
  manufacturerModel: true,
  classroom: false,
  receiptContent: true,
  visitDate: true,
  progressStatus: true,
  processResult: true,
  completedDate: true
});

/**
 * =========================
 * 컬럼 정의
 * =========================
 */
const columnDefinitions = [
  {
    key: "seq",
    label: "순번",
    className: "w-no",
    pdfClassName: "pdf-col-no",
    value: (row) => row.seq || "-"
  },
  {
    key: "receivedDate",
    label: "접수일",
    className: "w-date",
    pdfClassName: "pdf-col-date",
    value: (row) => row.receivedDate || "-"
  },
  {
    key: "schoolName",
    label: "학교명",
    className: "w-school",
    pdfClassName: "pdf-col-school",
    value: (row) => row.schoolName || "-"
  },
  {
    key: "customerName",
    label: "고객이름",
    className: "w-name",
    pdfClassName: "pdf-col-name",
    value: (row) => row.customerName || "-"
  },
  {
    key: "productName",
    label: "제품",
    className: "w-product",
    pdfClassName: "pdf-col-product",
    value: (row) => row.productName || "-"
  },
  {
    key: "manufacturerModel",
    label: "제조사(제품 모델명)",
    className: "w-model",
    pdfClassName: "pdf-col-model",
    value: (row) => row.manufacturerModel || "-",
    left: true
  },
  {
    key: "classroom",
    label: "교실",
    className: "w-classroom",
    pdfClassName: "pdf-col-classroom",
    value: (row) => row.classroom || "-"
  },
  {
    key: "receiptContent",
    label: "A/S접수내용",
    className: "w-content",
    pdfClassName: "pdf-col-content",
    value: (row) => row.receiptContent || "-",
    multiline: true,
    left: true,
    mobileBlock: true
  },
  {
    key: "visitDate",
    label: "방문일자",
    className: "w-date",
    pdfClassName: "pdf-col-date",
    value: (row) => row.visitDate || "-"
  },
  {
    key: "progressStatus",
    label: "진행여부",
    className: "w-progress",
    pdfClassName: "pdf-col-progress",
    value: (row) => row.progressStatus || "-"
  },
  {
    key: "processResult",
    label: "처리결과",
    className: "w-result",
    pdfClassName: "pdf-col-result",
    value: (row) => row.processResult || "-",
    multiline: true,
    left: true,
    mobileBlock: true
  },
  {
    key: "completedDate",
    label: "완료일자",
    className: "w-date",
    pdfClassName: "pdf-col-date",
    value: (row) => row.completedDate || "-"
  }
];

const visibleListColumns = computed(() =>
  columnDefinitions.filter((col) => listColumnVisible.value[col.key] !== false)
);

const visiblePdfColumns = computed(() =>
  columnDefinitions.filter((col) => pdfColumnVisible.value[col.key] !== false)
);

const visibleMobileMetaColumns = computed(() =>
  visibleListColumns.value.filter((col) => !col.mobileBlock)
);

const visibleMobileBlockColumns = computed(() =>
  visibleListColumns.value.filter((col) => col.mobileBlock)
);

function formatMonthValue(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0"
  )}`;
}

function getCurrentMonthDefaultRange() {
  const now = new Date();
  const currentMonth = formatMonthValue(now);

  return {
    start: currentMonth,
    end: currentMonth
  };
}

const defaultRange = getCurrentMonthDefaultRange();
const startMonth = ref(defaultRange.start);
const endMonth = ref(defaultRange.end);

const hasAnyResult = computed(() => {
  return techRows.value.length > 0 || blendRows.value.length > 0;
});

const pageTitle = computed(() => {
  return searchedSchoolName.value
    ? `[${searchedSchoolName.value}] 테크센터 IT기기 관리카드`
    : "테크센터 IT기기 관리카드";
});

const pdfTitle = computed(() => {
  return searchedSchoolName.value
    ? `[${searchedSchoolName.value}] 테크센터 IT기기 관리카드`
    : "테크센터 IT기기 관리카드";
});

const printedAt = computed(() => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  return `${y}.${m}.${d} ${hh}:${mm}`;
});

function cleanupPdfPreviewUrl() {
  if (pdfPreviewUrl.value) {
    URL.revokeObjectURL(pdfPreviewUrl.value);
    pdfPreviewUrl.value = "";
  }
}

onBeforeUnmount(() => {
  cleanupPdfPreviewUrl();
});

function convertMonthToApiFormat(value) {
  if (!value) return "";
  return value.replace("-", ".");
}

function pickValue(item, keys = [], defaultValue = "") {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return defaultValue;
}

function buildRowId(item, index = 0, prefix = "as") {
  const sourceTab = pickValue(item, ["출처탭"], "sample");
  const seq = pickValue(item, ["순번", "번호", "id"], index);
  return `${prefix}-${sourceTab}-${seq}-${index}`;
}

function normalizeProgressStatus(value) {
  const text = String(value || "").trim();
  if (text === "이관") return "진행중";
  return text || "";
}

function normalizeASRow(item, index = 0, serviceType = "tech") {
  const isBlend = serviceType === "blend";

  return {
    id: buildRowId(item, index, serviceType),
    serviceType,
    serviceLabel: isBlend ? "블랜디드" : "테크센터",

    seq: pickValue(item, ["순번"]),
    receivedDate: pickValue(item, ["접수일", "접수일자"]),
    schoolName: pickValue(item, ["학교명"]),
    customerName: pickValue(item, ["고객이름", "고객명", "이름"]),
    productName: pickValue(item, ["제품", "품목"]),
    manufacturerModel: pickValue(item, [
      "제조사(제품 모델명)",
      "제조사(모델명)",
      "제조사/모델명"
    ]),
    classroom: pickValue(item, ["교실", "교실/위치"]),
    receiptContent: pickValue(item, ["A/S접수내용", "접수내용"]),
    visitDate: pickValue(item, ["방문일자", "방문일"]),
    progressStatus: normalizeProgressStatus(
      pickValue(item, ["진행여부", "진행상태"])
    ),
    processResult: pickValue(item, ["처리결과", "조치결과", "점검결과"]),
    completedDate: pickValue(item, ["완료일자", "완료날짜", "완료일"]),

    raw: item
  };
}

async function getASRequest() {
  if (!schoolName.value) {
    techRows.value = [];
    blendRows.value = [];
    searched.value = false;
    searchedSchoolName.value = "";
    showToast?.("학교명을 입력해 주세요.");
    return;
  }

  if (!startMonth.value || !endMonth.value) {
    showToast?.("시작월과 종료월을 입력해 주세요.");
    return;
  }

  if (startMonth.value > endMonth.value) {
    showToast?.("시작월이 종료월보다 늦을 수 없습니다.");
    return;
  }

  isLoading.value = true;
  searched.value = true;
  techRows.value = [];
  blendRows.value = [];

  try {
    const params = {
      school_name: schoolName.value,
      u_name: "",
      receipt_date_from: convertMonthToApiFormat(startMonth.value),
      receipt_date_to: convertMonthToApiFormat(endMonth.value)
    };

    const result = await requestApi.selectASRequest(params);

    const techList = Array.isArray(result?.tech_as_list)
      ? result.tech_as_list
      : Array.isArray(result)
      ? result
      : [];

    const blendList = Array.isArray(result?.blend_as_list)
      ? result.blend_as_list
      : [];

    techRows.value = techList.map((item, index) =>
      normalizeASRow(item, index, "tech")
    );

    blendRows.value = blendList.map((item, index) =>
      normalizeASRow(item, index, "blend")
    );

    searchedSchoolName.value = schoolName.value;
  } catch (error) {
    console.error("A/S 관리 조회 실패:", error);
    techRows.value = [];
    blendRows.value = [];
    searchedSchoolName.value = "";
    showToast?.("조회 중 오류가 발생했습니다.");
  } finally {
    isLoading.value = false;
  }
}

async function handleSearch() {
  await getASRequest();
}

function resetSearch() {
  const currentDefault = getCurrentMonthDefaultRange();

  schoolName.value = "";
  searchedSchoolName.value = "";
  searched.value = false;
  techRows.value = [];
  blendRows.value = [];
  startMonth.value = currentDefault.start;
  endMonth.value = currentDefault.end;
}

function getPdfCharsPerLine(colKey) {
  const map = {
    seq: 6,
    receivedDate: 10,
    schoolName: 14,
    customerName: 10,
    productName: 12,
    manufacturerModel: 18,
    classroom: 10,
    receiptContent: 20,
    visitDate: 10,
    progressStatus: 8,
    processResult: 18,
    completedDate: 10
  };

  return map[colKey] || 12;
}

function buildPdfPagesForSection(sourceRows, sectionLabel) {
  if (!Array.isArray(sourceRows) || !sourceRows.length) return [];

  const rowsWithNumber = sourceRows.map((row, index) => ({
    ...row,
    rowNumber: index + 1,
    pdfRowId: `${sectionLabel}-${row.id}-${index}`
  }));

  const PAGE_CAPACITY = 820;
  const BASE_ROW_HEIGHT = 30;
  const LINE_HEIGHT = 13;

  const getLineCount = (text, charsPerLine) => {
    const value = String(text || "-");
    const lines = value.split("\n");

    return lines.reduce((sum, line) => {
      const clean = line || " ";
      return sum + Math.max(1, Math.ceil(clean.length / charsPerLine));
    }, 0);
  };

  const estimateRowHeight = (row) => {
    const visibleLines = visiblePdfColumns.value.map((col) => {
      return getLineCount(col.value(row), getPdfCharsPerLine(col.key));
    });

    const maxLines = Math.max(1, ...visibleLines);

    return BASE_ROW_HEIGHT + (maxLines - 1) * LINE_HEIGHT;
  };

  const pages = [];
  let currentRows = [];
  let currentHeight = 0;

  rowsWithNumber.forEach((row) => {
    const rowHeight = estimateRowHeight(row);

    if (currentRows.length && currentHeight + rowHeight > PAGE_CAPACITY) {
      pages.push({
        sectionLabel,
        rows: currentRows
      });
      currentRows = [row];
      currentHeight = rowHeight;
    } else {
      currentRows.push(row);
      currentHeight += rowHeight;
    }
  });

  if (currentRows.length) {
    pages.push({
      sectionLabel,
      rows: currentRows
    });
  }

  return pages;
}

async function preparePdfPages() {
  const blendPages = buildPdfPagesForSection(blendRows.value, "블랜디드");
  const techPages = buildPdfPagesForSection(techRows.value, "테크센터");

  pdfPageChunks.value = [...blendPages, ...techPages];

  if (!pdfPageChunks.value.length) {
    pdfPageChunks.value = [
      {
        sectionLabel: "조회결과",
        rows: []
      }
    ];
  }

  await nextTick();
}

async function renderPdfBlob() {
  const pageContainer = pdfCapturePagesRef.value;

  if (!pageContainer) {
    throw new Error("PDF 캡처 대상이 없습니다.");
  }

  const pageElements = Array.from(pageContainer.querySelectorAll(".pdf-page"));

  if (!pageElements.length) {
    throw new Error("PDF 페이지가 생성되지 않았습니다.");
  }

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4"
  });

  const pdfWidth = 297;
  const pdfHeight = 210;
  const margin = 5;
  const availableWidth = pdfWidth - margin * 2;
  const availableHeight = pdfHeight - margin * 2;

  for (let i = 0; i < pageElements.length; i += 1) {
    const pageEl = pageElements[i];

    const canvas = await html2canvas(pageEl, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff"
    });

    const imgData = canvas.toDataURL("image/png");
    const imgProps = pdf.getImageProperties(imgData);

    const ratio = Math.min(
      availableWidth / imgProps.width,
      availableHeight / imgProps.height
    );

    const renderWidth = imgProps.width * ratio;
    const renderHeight = imgProps.height * ratio;
    const x = (pdfWidth - renderWidth) / 2;
    const y = (pdfHeight - renderHeight) / 2;

    if (i > 0) {
      pdf.addPage();
    }

    pdf.addImage(imgData, "PNG", x, y, renderWidth, renderHeight);
  }

  return pdf.output("blob");
}

function getPdfFileName() {
  const keyword = searchedSchoolName.value?.trim() || "검색결과";
  return `[${keyword}]_테크센터_IT기기_관리카드.pdf`;
}

async function openPdfPreview() {
  if (!hasAnyResult.value) {
    showToast?.("미리보기할 검색 결과가 없습니다.");
    return;
  }

  try {
    pdfModalVisible.value = true;
    await nextTick();

    await preparePdfPages();
    cleanupPdfPreviewUrl();

    const blob = await renderPdfBlob();
    pdfPreviewUrl.value = URL.createObjectURL(blob);
  } catch (error) {
    console.error("PDF 미리보기 생성 실패:", error);
    showToast?.("PDF 미리보기를 생성하지 못했습니다.");
  }
}

function closePdfPreview() {
  pdfModalVisible.value = false;
  pdfPageChunks.value = [];
  cleanupPdfPreviewUrl();
}

async function downloadPdf() {
  if (isDownloadingPdf.value) return;

  try {
    isDownloadingPdf.value = true;

    if (!pdfPageChunks.value.length) {
      await preparePdfPages();
    }

    const blob = await renderPdfBlob();
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = getPdfFileName();
    document.body.appendChild(link);
    link.click();
    link.remove();

    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("PDF 다운로드 실패:", error);
    showToast?.("PDF 다운로드 중 오류가 발생했습니다.");
  } finally {
    isDownloadingPdf.value = false;
  }
}
</script>

<style scoped>
.page {
  min-height: 100vh;
  background: #f4f7fb;
  color: #1f2937;
  padding: 24px 20px 40px;
  box-sizing: border-box;
}

.page-inner {
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.page-title {
  margin: 0;
  font-size: 28px;
  font-weight: 800;
}

.sub {
  margin: 6px 0 0;
  color: #6b7280;
  font-size: 14px;
  line-height: 1.5;
}

.card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 18px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.05);
}

.search-card {
  padding: 18px;
}

.search-grid {
  display: grid;
  grid-template-columns: minmax(260px, 1.5fr) 200px 240px auto;
  gap: 18px;
  align-items: end;
}

.search-field label {
  display: block;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #374151;
}

.input {
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  background: #fff;
}

.input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 4px rgba(37, 99, 235, 0.08);
}

.month-input {
  min-width: 0;
}

.month-end-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
}

.range-tilde {
  flex: 0 0 auto;
  font-size: 18px;
  font-weight: 800;
  color: #6b7280;
  line-height: 1;
}

.search-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.btn {
  height: 44px;
  padding: 0 18px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid transparent;
  transition: 0.2s ease;
  white-space: nowrap;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #2563eb;
  color: #fff;
}

.btn-primary:hover:not(:disabled) {
  background: #1d4ed8;
}

.btn-outline {
  background: #fff;
  color: #374151;
  border-color: #d1d5db;
}

.btn-outline:hover:not(:disabled) {
  background: #f9fafb;
}

.btn-dark {
  background: #111827;
  color: #fff;
}

.btn-dark:hover:not(:disabled) {
  background: #0f172a;
}

.state-card {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-weight: 600;
}

.result-section {
  overflow: hidden;
  padding: 12px 0 8px;
}

.section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 0 18px 12px;
}

.section-title {
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #111827;
}

.section-count {
  font-size: 14px;
  color: #6b7280;
  font-weight: 700;
}

.desktop-only {
  display: block;
}

.mobile-only {
  display: none !important;
}

.table-wrap {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 0 12px 8px;
}

.intake-table {
  width: 100%;
  /* min-width: 2200px; */
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

.intake-table thead th {
  position: sticky;
  top: 0;
  z-index: 3;
  background: #f8fafc;
  color: #475569;
  font-size: 12px;
  font-weight: 800;
  text-align: left;
  padding: 12px 10px;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.intake-table tbody td {
  padding: 12px 10px;
  border-bottom: 1px solid #eef2f7;
  vertical-align: top;
  font-size: 13px;
  background: #ffffff;
  line-height: 1.5;
}

.data-row td {
  transition: background 0.2s ease;
}

.data-row:hover td {
  background: #f8fafc;
}

.cell-text {
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.multiline-text {
  white-space: pre-wrap;
  word-break: break-word;
  overflow-wrap: anywhere;
}

.loading-box {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  font-weight: 600;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid #dbe3ec;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;
}

.mobile-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 12px 12px;
}

.mobile-item {
  padding: 16px;
  border-radius: 18px;
}

.mobile-top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
  align-items: flex-start;
}

.mobile-title {
  font-size: 16px;
  font-weight: 800;
  color: #111827;
}

.mobile-meta {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 12px;
  font-size: 13px;
  color: #4b5563;
  margin-bottom: 12px;
}

.mobile-meta b {
  color: #111827;
  margin-right: 4px;
}

.mobile-block {
  margin-top: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 12px;
}

.mobile-label {
  font-size: 12px;
  font-weight: 800;
  color: #475569;
  margin-bottom: 6px;
}

.mobile-value {
  font-size: 13px;
  color: #374151;
  white-space: pre-wrap;
  word-break: break-word;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  box-sizing: border-box;
}

.modal-card {
  width: min(1280px, 100%);
  height: min(92vh, 920px);
  background: #ffffff;
  border-radius: 22px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 20px 60px rgba(15, 23, 42, 0.22);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.modal-header {
  padding: 18px 22px 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.modal-title {
  margin: 0;
  font-size: 20px;
  font-weight: 800;
  color: #111827;
}

.modal-sub {
  margin: 6px 0 0;
  font-size: 13px;
  color: #6b7280;
}

.modal-close {
  width: 40px;
  height: 40px;
  border: 1px solid #d1d5db;
  background: #fff;
  color: #374151;
  border-radius: 12px;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
}

.modal-close:hover {
  background: #f9fafb;
}

.modal-body {
  position: relative;
  flex: 1;
  background: #f8fafc;
  overflow: hidden;
}

.pdf-frame {
  width: 100%;
  height: 100%;
  border: none;
  background: #ffffff;
}

.pdf-loading {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.capture-area {
  position: fixed;
  left: -99999px;
  top: 0;
  width: 2000px;
  background: #fff;
  z-index: -1;
}

.pdf-capture-stack {
  width: 2000px;
  background: #fff;
}

.pdf-page {
  width: 2000px;
  min-height: 990px;
  background: #ffffff;
  padding: 18px 20px 14px;
  box-sizing: border-box;
  color: #111827;
  page-break-after: always;
}

.pdf-page-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.pdf-title {
  font-size: 24px;
  font-weight: 800;
  text-align: center;
  flex: 1;
}

.pdf-page-meta {
  min-width: 70px;
  text-align: right;
  font-size: 14px;
  font-weight: 700;
  color: #374151;
}

.pdf-section-label {
  margin-bottom: 10px;
  font-size: 16px;
  font-weight: 800;
  color: #111827;
}

.pdf-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background: #fff;
}

.pdf-table th,
.pdf-table td {
  border: 1px solid #222;
  padding: 6px 5px;
  font-size: 10px;
  line-height: 1.35;
  vertical-align: top;
  text-align: center;
  word-break: break-word;
}

.pdf-table th {
  background: #e5e5e5;
  font-weight: 700;
}

.pdf-table .left {
  text-align: left;
}

.pdf-table .pre {
  white-space: pre-wrap;
}

.pdf-footer {
  margin-top: 8px;
  text-align: right;
  font-size: 11px;
  color: #4b5563;
}

.modal-footer {
  padding: 16px 22px 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* .w-no {
  width: 5%;
} */
.w-date {
  /* 접수일, 방문일자, 완료일자 */
  width: 8%;
}
/* .w-school {
  width: 12%;
}
.w-name {
  width: 8%;
} */
.w-product {
  /* 제품 */
  width: 10.5%;
}
.w-model {
  /* 제조사(제품 모델명) */
  width: 10.5%;
}
/* .w-classroom {
  width: 8%;
} */
.w-content {
  /* 접수내용 */
  width: 23.5%;
}
.w-progress {
  /* 진행여부 */
  width: 8%;
}
.w-result {
  /* 처리결과 */
  width: 23.5%;
}

/* .pdf-col-no {
  width: 4%;
} */
.pdf-col-date {
  width: 8%;
}
/* .pdf-col-school {
  width: 11%;
}
.pdf-col-name {
  width: 7%;
} */
.pdf-col-product {
  width: 10.5%;
}
.pdf-col-model {
  width: 10.5%;
}
/* .pdf-col-classroom {
  width: 7%;
} */
.pdf-col-content {
  width: 23.5%;
}
.pdf-col-progress {
  width: 8%;
}
.pdf-col-result {
  width: 23.5%;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  border: 0;
  clip: rect(0, 0, 0, 0);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1280px) {
  .search-grid {
    grid-template-columns: 1fr 1fr;
  }

  .search-actions {
    grid-column: 1 / -1;
  }
}

@media (max-width: 900px) {
  .page {
    padding: 16px 12px 28px;
  }

  .page-header {
    flex-direction: column;
  }

  .page-title {
    font-size: 24px;
  }

  .desktop-only {
    display: none !important;
  }

  .mobile-only {
    display: block !important;
  }

  .search-grid {
    grid-template-columns: 1fr;
  }

  .month-end-wrap {
    width: 100%;
  }

  .search-actions {
    width: 100%;
  }

  .search-actions .btn {
    flex: 1;
  }

  .mobile-meta {
    grid-template-columns: 1fr;
  }

  .modal-overlay {
    padding: 12px;
  }

  .modal-card {
    width: 100%;
    height: 92vh;
    border-radius: 18px;
  }

  .modal-header,
  .modal-footer {
    padding-left: 16px;
    padding-right: 16px;
  }
}

@media (max-width: 640px) {
  .page {
    padding: 12px 10px 24px;
  }

  .mobile-item {
    padding: 14px;
  }

  .mobile-title {
    font-size: 15px;
  }

  .mobile-meta,
  .mobile-value {
    font-size: 12px;
  }

  .modal-title {
    font-size: 18px;
  }

  .modal-sub {
    font-size: 12px;
  }

  .modal-footer {
    flex-wrap: wrap;
  }

  .modal-footer .btn {
    flex: 1;
  }

  .month-end-wrap {
    gap: 10px;
  }

  .range-tilde {
    font-size: 16px;
  }
}
</style>
